/**
 * Sky, lighting and image-based reflections.
 *
 * Each track ships a `TrackEnvironment` (see src/types.ts) describing its mood:
 * sky gradient, sun direction and colour, fog, exposure. This module owns the
 * objects that mood drives — a gradient sky dome with a real sun disc, the three
 * lights, the scene fog, and a PMREM environment map rendered FROM that same sky
 * so chrome taps, porcelain tubs and wire baskets reflect the actual horizon
 * instead of a flat grey.
 *
 * The dome is re-centred on the camera every frame, so it behaves as an infinite
 * backdrop; the sun rides along with the player so its shadow map stays tight.
 */
import * as THREE from 'three';

import type { TrackEnvironment } from './types';

const SKY_VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const SKY_FRAG = /* glsl */ `
uniform vec3 uTop;
uniform vec3 uHorizon;
uniform vec3 uGround;
uniform vec3 uSunColor;
uniform vec3 uSunDir;
uniform float uSunVisible;
varying vec3 vDir;

void main() {
  vec3 d = normalize(vDir);

  // Vertical gradient: horizon -> zenith above, horizon -> haze below.
  vec3 col = mix(uHorizon, uTop, pow(clamp(d.y, 0.0, 1.0), 0.55));
  col = mix(col, uGround, smoothstep(0.0, -0.25, d.y));

  // Sun: a tight disc sitting inside a broad atmospheric glow.
  float ang = max(dot(d, normalize(uSunDir)), 0.0);
  float disc = smoothstep(0.9993, 0.9997, ang);
  float glow = pow(ang, 320.0) * 0.9 + pow(ang, 9.0) * 0.14;
  col += uSunColor * (glow + disc * 8.0) * uSunVisible;

  gl_FragColor = vec4(col, 1.0);

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** Radius of the sky dome — inside the camera's far plane, outside everything else. */
const SKY_RADIUS = 1500;
/** How far the sun sits from the kart. Bounds the shadow camera. */
const SUN_DISTANCE = 130;
/** Half-extent of the sun's orthographic shadow frustum, in world units. */
const SHADOW_SPAN = 62;

export class Environment {
  /** Current mood; `apply()` replaces it. */
  private env: TrackEnvironment | null = null;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;

  private readonly skyMaterial: THREE.ShaderMaterial;
  private readonly sky: THREE.Mesh;

  readonly sun: THREE.DirectionalLight;
  private readonly hemi: THREE.HemisphereLight;
  private readonly ambient: THREE.AmbientLight;

  // Image-based lighting rendered from the sky dome itself.
  private readonly pmrem: THREE.PMREMGenerator;
  private readonly iblScene: THREE.Scene;
  private iblTarget: THREE.WebGLRenderTarget | null = null;

  private readonly sunDir = new THREE.Vector3(0.5, 0.7, 0.35);

  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
    this.scene = scene;
    this.renderer = renderer;

    this.skyMaterial = new THREE.ShaderMaterial({
      vertexShader: SKY_VERT,
      fragmentShader: SKY_FRAG,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      uniforms: {
        uTop: { value: new THREE.Color(0x2a6ad4) },
        uHorizon: { value: new THREE.Color(0xbcd8f2) },
        uGround: { value: new THREE.Color(0x6d7a86) },
        uSunColor: { value: new THREE.Color(0xfff0d0) },
        uSunDir: { value: this.sunDir },
        uSunVisible: { value: 1 },
      },
    });

    const domeGeo = new THREE.SphereGeometry(SKY_RADIUS, 32, 24);
    this.sky = new THREE.Mesh(domeGeo, this.skyMaterial);
    this.sky.frustumCulled = false;
    this.sky.renderOrder = -1000; // draw first, everything else overwrites it
    scene.add(this.sky);

    // A unit-scale twin of the dome, rendered into a cube map for reflections.
    this.iblScene = new THREE.Scene();
    this.iblScene.add(new THREE.Mesh(new THREE.SphereGeometry(10, 32, 24), this.skyMaterial));

    this.hemi = new THREE.HemisphereLight(0xbfe3ff, 0x3a5a2a, 0.75);
    scene.add(this.hemi);

    this.ambient = new THREE.AmbientLight(0xffffff, 0.2);
    scene.add(this.ambient);

    this.sun = new THREE.DirectionalLight(0xfff4e0, 2.4);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = SUN_DISTANCE * 2.2;
    this.sun.shadow.camera.left = -SHADOW_SPAN;
    this.sun.shadow.camera.right = SHADOW_SPAN;
    this.sun.shadow.camera.top = SHADOW_SPAN;
    this.sun.shadow.camera.bottom = -SHADOW_SPAN;
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = 0.03;
    scene.add(this.sun, this.sun.target);

    this.pmrem = new THREE.PMREMGenerator(renderer);
  }

  /** Switch to a track's mood: sky, lights, fog, exposure and reflections. */
  apply(env: TrackEnvironment): void {
    this.env = env;
    const u = this.skyMaterial.uniforms;
    (u.uTop.value as THREE.Color).setHex(env.skyTop);
    (u.uHorizon.value as THREE.Color).setHex(env.skyHorizon);
    (u.uGround.value as THREE.Color).setHex(env.skyGround);
    (u.uSunColor.value as THREE.Color).setHex(env.sunColor);
    u.uSunVisible.value = env.sunVisible ? 1 : 0;
    this.sunDir.copy(env.sunDirection).normalize();

    this.sun.color.setHex(env.sunColor);
    this.sun.intensity = env.sunIntensity;
    this.hemi.color.setHex(env.hemiSky);
    this.hemi.groundColor.setHex(env.hemiGround);
    this.hemi.intensity = env.hemiIntensity;
    this.ambient.intensity = env.ambientIntensity;

    this.scene.fog = new THREE.FogExp2(env.fogColor, env.fogDensity);
    this.renderer.toneMappingExposure = env.exposure;

    this.rebuildIBL();
  }

  /**
   * Re-render the sky into a prefiltered cube map and hand it to the scene as
   * `environment`, so every PBR material picks up sky-coloured reflections.
   */
  private rebuildIBL(): void {
    const previous = this.iblTarget;
    this.iblTarget = this.pmrem.fromScene(this.iblScene, 0, 1, 60);
    this.scene.environment = this.iblTarget.texture;
    // The sky already lights the scene through the hemisphere light; keep the
    // IBL contribution as reflections rather than a second fill.
    this.scene.environmentIntensity = 0.55;
    previous?.dispose();
  }

  /**
   * Per-frame: pin the dome to the camera and park the sun over `focus` so its
   * shadow frustum always covers the action.
   */
  update(camera: THREE.Camera, focus: THREE.Vector3): void {
    this.sky.position.copy(camera.position);
    this.sun.position.copy(focus).addScaledVector(this.sunDir, SUN_DISTANCE);
    this.sun.target.position.copy(focus);
    this.sun.target.updateMatrixWorld();
  }

  /** Bloom strength the current mood asks for (0 = disabled). */
  get bloom(): number {
    return this.env?.bloom ?? 0;
  }

  /**
   * Apply a quality preset's shadow settings. Resizing the map disposes the old
   * one so the renderer allocates at the new resolution on the next frame.
   */
  setShadowQuality(enabled: boolean, mapSize: number): void {
    this.sun.castShadow = enabled;
    if (this.sun.shadow.mapSize.width === mapSize) return;
    this.sun.shadow.mapSize.set(mapSize, mapSize);
    this.sun.shadow.map?.dispose();
    this.sun.shadow.map = null;
  }

  dispose(): void {
    this.sky.geometry.dispose();
    this.skyMaterial.dispose();
    this.iblTarget?.dispose();
    this.pmrem.dispose();
  }
}
